import { useEffect, useMemo, useState, useRef } from "react";
import { useAuth } from "../services/authService";
import { FaArrowLeft, FaArrowRight } from "react-icons/fa";
import { db } from "../services/firebase";
import {
  collection,
  query,
  orderBy,
  getDocs,
  deleteDoc,
  doc,
  where,
  limit,
  startAfter,
} from "firebase/firestore";
import "./AdminDownloadLogs.scss";

function formatTs(ts) {
  if (!ts) return { date: "—", time: "—" };
  try {
    const d =
      typeof ts?.toDate === "function"
        ? ts.toDate()
        : ts?.seconds
          ? new Date(ts.seconds * 1000)
          : new Date(ts);
    return {
      date: d.toLocaleDateString("cs-CZ"),
      time: d.toLocaleTimeString("cs-CZ"),
    };
  } catch {
    return { date: "—", time: "—" };
  }
}

export default function AdminDownloadLogs() {
  const { userRole } = useAuth();
  const topRef = useRef(null);
  const PAGE_SIZE = 5;

  const [logs, setLogs] = useState([]);
  const [search, setSearch] = useState("");
  const [usersByEmail, setUsersByEmail] = useState({});

  const [loading, setLoading] = useState(true);
  const [busyEmail, setBusyEmail] = useState(null);
  const [error, setError] = useState("");

  // paging
  const [page, setPage] = useState(1);
  const [pageCursors, setPageCursors] = useState([]); // lastDoc per page
  const [hasNext, setHasNext] = useState(false);

  const modeLabel = useMemo(() => {
    if (userRole === "client_admin") return "Režim: client_admin";
    if (userRole === "super_admin") return "Režim: superadmin (vidí vše)";
    return "";
  }, [userRole]);

  useEffect(() => {
    loadUsersMapOnce();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Object.keys(usersByEmail).length]);

  const loadUsersMapOnce = async () => {
    try {
      const usersSnap = await getDocs(collection(db, "users"));
      const map = {};
      usersSnap.docs.forEach((d) => {
        const data = d.data() || {};
        if (!data.email) return;
        const fullName = `${data.name || ""} ${data.surname || ""}`.trim();
        map[data.email] = fullName || "(bez jména)";
      });
      setUsersByEmail(map);
    } catch (e) {
      console.warn("loadUsersMapOnce failed:", e);
      setUsersByEmail({});
    }
  };

  const fetchPage = async (targetPage = 1) => {
    setLoading(true);
    setError("");
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: "smooth" });
    }

    try {
      const logsRef = collection(db, "downloadLogs");

      const cursor =
        targetPage <= 1 ? null : pageCursors[targetPage - 2] || null;

      const constraints = cursor
        ? [
            orderBy("timestamp", "desc"),
            startAfter(cursor),
            limit(PAGE_SIZE + 1),
          ]
        : [orderBy("timestamp", "desc"), limit(PAGE_SIZE + 1)];

      const logsQuery = query(logsRef, ...constraints);
      const snap = await getDocs(logsQuery);

      const docs = snap.docs || [];
      const docsForPage = docs.slice(0, PAGE_SIZE);
      setHasNext(docs.length > PAGE_SIZE);

      const lastDoc = docsForPage.length
        ? docsForPage[docsForPage.length - 1]
        : null;

      setPageCursors((prev) => {
        const next = [...prev];
        next[targetPage - 1] = lastDoc;
        return next;
      });

      const rawLogs = docsForPage.map((d) => ({ id: d.id, ...d.data() }));

      const enriched = rawLogs.map((log) => {
        const { date, time } = formatTs(log.timestamp);
        return {
          ...log,
          name: usersByEmail[log.userEmail] || "(neznámý uživatel)",
          date,
          time,
        };
      });

      setLogs(enriched);
      setPage(targetPage);
    } catch (e) {
      console.error("Chyba při načítání logů:", e);
      setError("Chyba při načítání logů.");
      setLogs([]);
      setHasNext(false);
    } finally {
      setLoading(false);
    }
  };

  const deleteLogsByEmail = async (email) => {
    const confirmDelete = window.confirm(
      `Opravdu chcete smazat všechny logy uživatele s e-mailem ${email}? Uživatelský účet zůstane zachován.`,
    );
    if (!confirmDelete) return;

    setBusyEmail(email);
    setError("");

    try {
      const logsQuery = query(
        collection(db, "downloadLogs"),
        where("userEmail", "==", email),
      );
      const logsSnapshot = await getDocs(logsQuery);

      const batchDeletions = logsSnapshot.docs.map((docSnap) =>
        deleteDoc(doc(db, "downloadLogs", docSnap.id)),
      );
      await Promise.all(batchDeletions);

      await fetchPage(page);

      alert("Logy uživatele byly úspěšně smazány.");
    } catch (error) {
      console.error("Chyba při mazání logů:", error);
      setError("Došlo k chybě při mazání logů.");
      alert("Došlo k chybě při mazání logů.");
    } finally {
      setBusyEmail(null);
    }
  };

  const goPrev = () => {
    if (page <= 1) return;
    fetchPage(page - 1);
  };

  const goNext = () => {
    if (!hasNext) return;
    fetchPage(page + 1);
  };

  const filteredLogs = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return logs;
    return logs.filter((log) => {
      const hay = `${log.name} ${log.userEmail} ${log.filename}`.toLowerCase();
      return hay.includes(q);
    });
  }, [logs, search]);

  return (
    <div className="admin-logs-page" ref={topRef}>
      <h1>Přehled stažených dokumentů</h1>

      {modeLabel && <p className="mode-line">{modeLabel}</p>}

      <div className="admin-search">
        <label htmlFor="logsSearch">Hledat:</label>
        <input
          id="logsSearch"
          type="text"
          placeholder="jméno / email / soubor"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {error && <p style={{ color: "crimson" }}>{error}</p>}

      <div className="table-content-wrapper">
        {loading ? (
          <p>Načítání záznamů...</p>
        ) : filteredLogs.length === 0 ? (
          <p>Žádné záznamy.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Jméno:</th>
                <th>Email:</th>
                <th>Soubor:</th>
                <th>Datum:</th>
                <th>Čas:</th>
                <th>Akce:</th>
              </tr>
            </thead>

            <tbody>
              {filteredLogs.map((log) => (
                <tr key={log.id}>
                  <td>
                    <span className="label">Jméno:</span>
                    <span className="value">{log.name}</span>
                  </td>

                  <td>
                    <span className="label">Email:</span>
                    <span className="value">{log.userEmail}</span>
                  </td>

                  <td>
                    <span className="label">Soubor:</span>
                    <span className="value">{log.filename}</span>
                  </td>

                  <td>
                    <span className="label">Datum:</span>
                    <span className="value">{log.date}</span>
                  </td>

                  <td>
                    <span className="label">Čas:</span>
                    <span className="value">{log.time}</span>
                  </td>

                  <td>
                    <span className="label">Akce:</span>
                    <span className="value">
                      <div className="action-buttons">
                        <button
                          className="delete"
                          disabled={busyEmail === log.userEmail}
                          onClick={() => deleteLogsByEmail(log.userEmail)}
                        >
                          {busyEmail === log.userEmail ? "..." : "Smazat"}
                        </button>
                      </div>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {(logs.length > 0 || page > 1) && !loading && (
        <div className="pagination-wrapper">
          <button
            className="pagination-btn"
            onClick={goPrev}
            disabled={page <= 1 || loading}
          >
            <FaArrowLeft className="icon" />
            <span className="text">Předchozí</span>
          </button>
          <div className="page-info">
            Stránka <b>{page}</b>
          </div>
          <button
            className="pagination-btn"
            onClick={goNext}
            disabled={!hasNext || loading}
          >
            <FaArrowRight className="icon" />
            <span className="text">Další</span>
          </button>
        </div>
      )}
    </div>
  );
}
